import { ColumnsType } from 'antd/es/table';
import React from 'react';
import { FormatTime } from 'components/organisms/timesheets/common/format-time';
import { BaseCell } from 'components/organisms/timesheets/common/timesheet-table';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { dateSorter, numberSorter } from 'utils/sorters';
import {
  ColumnHeader,
  commonColumns,
  DateCell,
  DaysDifferenceCell,
  DifferenceCell,
  HoursDifferenceCell,
  LastFiveUpdateCell,
  minutesToRoundedHours,
  NumberCell,
  projectNameColumn,
} from '../../common';
import { ProjectsReportRecord } from '../../types';

interface Params {
  noOfRows: number;
}

export const useTimelineViewColumns = ({
  noOfRows,
}: Params): ColumnsType<ProjectsReportRecord> => {
  const translate = useTranslate();
  const [isTimesheetFeatureEnabled] = useAccountFeature(
    AccountFeature.Timesheets,
  );

  return [
    projectNameColumn(noOfRows, translate),
    {
      titleText: useTranslation('reports.columns.start_date.planned'),
      key: 'startDatePlanned',
      render: (_, { startDate }: ProjectsReportRecord) => (
        <DateCell date={startDate} />
      ),
      sorter: (
        { startDate: a }: ProjectsReportRecord,
        { startDate: b }: ProjectsReportRecord,
      ) => dateSorter(a, b),
    },
    {
      titleText: useTranslation('reports.columns.start_date.actual'),
      description: useTranslation('reports.columns.start_date.actual.tooltip'),
      key: 'startDateActual',
      render: (_, { startDateActual }: ProjectsReportRecord) => (
        <DateCell date={startDateActual} />
      ),
      sorter: (
        { startDateActual: a }: ProjectsReportRecord,
        { startDateActual: b }: ProjectsReportRecord,
      ) => dateSorter(a, b),
    },
    {
      titleText: useTranslation('reports.columns.start_date.delay'),
      description: useTranslation('reports.columns.start_date.delay.tooltip'),
      key: 'startDateDelay',
      render: (_, { startingDelay }: ProjectsReportRecord) => (
        <DaysDifferenceCell value={startingDelay} />
      ),
      sorter: (
        { startingDelay: a }: ProjectsReportRecord,
        { startingDelay: b }: ProjectsReportRecord,
      ) => numberSorter(a, b),
    },
    {
      titleText: useTranslation('reports.columns.end_date.planned'),
      key: 'dueDatePlanned',
      render: (_, { dueDate }: ProjectsReportRecord) => (
        <DateCell date={dueDate} />
      ),
      sorter: (
        { dueDate: a }: ProjectsReportRecord,
        { dueDate: b }: ProjectsReportRecord,
      ) => dateSorter(a, b),
    },
    {
      titleText: useTranslation('reports.columns.end_date.actual'),
      description: useTranslation('reports.columns.end_date.actual.tooltip'),
      key: 'dueDateActual',
      render: (_, { dueDateActual }: ProjectsReportRecord) => (
        <DateCell date={dueDateActual} />
      ),
      sorter: (
        { dueDateActual: a }: ProjectsReportRecord,
        { dueDateActual: b }: ProjectsReportRecord,
      ) => dateSorter(a, b),
    },
    {
      titleText: useTranslation('reports.columns.end_date.delay'),
      description: useTranslation('reports.columns.end_date.delay.tooltip'),
      render: (_, { endingDelay }: ProjectsReportRecord) => (
        <DaysDifferenceCell value={endingDelay} />
      ),
      sorter: (
        { endingDelay: a }: ProjectsReportRecord,
        { endingDelay: b }: ProjectsReportRecord,
      ) => numberSorter(a, b),
    },
    {
      titleText: useTranslation('reports.columns.duration.planned'),
      description: useTranslation('reports.columns.duration.planned.tooltip'),
      key: 'durationPlanned',
      render: (_, { durationPlanned }: ProjectsReportRecord) => (
        <NumberCell value={durationPlanned} />
      ),
      sorter: (
        { durationPlanned: a }: ProjectsReportRecord,
        { durationPlanned: b }: ProjectsReportRecord,
      ) => numberSorter(a, b),
    },
    {
      titleText: useTranslation('reports.columns.duration.actual'),
      description: useTranslation('reports.columns.duration.actual.tooltip'),
      key: 'durationActual',
      render: (_, { durationActual }: ProjectsReportRecord) => (
        <NumberCell value={durationActual} />
      ),
      sorter: (
        { durationPlanned: a }: ProjectsReportRecord,
        { durationPlanned: b }: ProjectsReportRecord,
      ) => numberSorter(a, b),
    },
    {
      titleText: useTranslation('reports.columns.duration.difference'),
      description: useTranslation(
        'reports.columns.duration.difference.tooltip',
      ),
      key: 'durationDifference',
      render: (_, { durationDifference }: ProjectsReportRecord) => (
        <DifferenceCell value={durationDifference} />
      ),
      sorter: (
        { durationDifference: a }: ProjectsReportRecord,
        { durationDifference: b }: ProjectsReportRecord,
      ) => numberSorter(a, b),
    },
    {
      titleText: translate('reports.columns.budgeted_hours'),
      key: 'budgetedHours',
      render: (_, { budgetedHours }: ProjectsReportRecord) => (
        <BaseCell>
          <FormatTime value={budgetedHours ? budgetedHours * 60 : 0} />
        </BaseCell>
      ),
      sorter: (
        { budgetedHours: a }: ProjectsReportRecord,
        { budgetedHours: b }: ProjectsReportRecord,
      ) => numberSorter(a, b),
    },
    {
      titleText: translate('reports.columns.allocated.hours'),
      key: 'allocatedHours',
      render: (_, { allocationInfo: { minutes } }: ProjectsReportRecord) => (
        <BaseCell>
          <FormatTime value={minutes} />
        </BaseCell>
      ),
      sorter: (
        { allocationInfo: { minutes: a } }: ProjectsReportRecord,
        { allocationInfo: { minutes: b } }: ProjectsReportRecord,
      ) => numberSorter(a, b),
    },
    ...(isTimesheetFeatureEnabled
      ? [
          {
            titleText: translate('reports.columns.total_tracked_hours'),
            key: 'totalTrackedHours',
            render: (_, { totalTrackedHours }: ProjectsReportRecord) => (
              <BaseCell>
                <FormatTime value={totalTrackedHours} />
              </BaseCell>
            ),
            sorter: (
              { totalTrackedHours: a }: ProjectsReportRecord,
              { totalTrackedHours: b }: ProjectsReportRecord,
            ) => numberSorter(a, b),
          },
          {
            titleText: translate('reports.columns.hours_difference'),
            description: translate('reports.columns.hours_difference.tooltip'),

            key: 'hoursDifference',
            render: (_, { hoursDifference }: ProjectsReportRecord) => (
              <HoursDifferenceCell
                value={
                  hoursDifference != null
                    ? minutesToRoundedHours(hoursDifference)
                    : hoursDifference
                }
              />
            ),
            sorter: (
              { hoursDifference: a }: ProjectsReportRecord,
              { hoursDifference: b }: ProjectsReportRecord,
            ) => numberSorter(a, b),
          },
        ]
      : []),
    ...commonColumns(translate),
    {
      titleText: useTranslation('reports.columns.last_5_updates'),
      key: 'lastFiveUpdates',
      render: (
        _,
        { pastProjectStatuses, projectId, projectName }: ProjectsReportRecord,
      ) => (
        <LastFiveUpdateCell
          projectId={projectId}
          projectName={projectName}
          pastProjectStatuses={pastProjectStatuses}
        />
      ),
    },
  ].map((x) => ({
    showSorterTooltip: false,
    width: '224px',
    title: <ColumnHeader title={x.titleText} description={x.description} />,
    ...x,
  }));
};
